import type { HorasObra, NominaSemanal, ReporteSemanal, Subcontrato } from '@adelante/shared';

export interface ManoObraFila {
  codigo: string;
  hrs: number;
  pctHoras: number;
  nominaAsig: number;
  sc: number;
  moTotal: number;
  m2: number;
  costoM2: number;
}

export interface ManoObraCalc {
  tieneNomina: boolean;
  teorico: number;
  m2Sem: number;
  hhDirectas: number;
  hhEquivSubc: number;
  costoPromHH: number;
  hhM2Sin: number;
  hhM2Con: number;
  costoMOm2: number;
  eficiencia: number;
  moDirecta: number;
  moSubcontrato: number;
  moTotalGastada: number;
  moPresupuestada: number;
  sobrecosto: number;
  obras: ManoObraFila[];
}

/**
 * Cálculo de Mano de Obra de una semana (indicadores + distribución por obra).
 * Función pura compartida por el tab de reporte y el export a Excel, para no
 * duplicar las fórmulas. El `reporte` ya viene filtrado por estado de venta.
 */
export function calcularManoObra(
  semana: number,
  reporte: ReporteSemanal,
  nominaArr: NominaSemanal[],
  horasArr: HorasObra[],
  subcArr: Subcontrato[],
): ManoObraCalc {
  const sem = Number(semana);
  const nomina = nominaArr.find((n) => Number(n.semana_operativa_id) === sem);
  const horas = horasArr.filter((h) => Number(h.semana_operativa_id) === sem);
  const subc = subcArr.filter((s) => Number(s.semana_operativa_id) === sem);

  const montoNomina = nomina ? Number(nomina.monto_nomina_directa) : 0;
  const teorico = nomina ? Number(nomina.costo_teorico_m2) : 0;

  // Solo cuentan las obras presentes en el reporte (ya filtrado por venta).
  const obrasReporte = new Set(reporte.obras.map((o) => o.codigo));
  const m2PorObra = new Map(reporte.obras.map((o) => [o.codigo, o.m2_semana]));
  const enReporte = (codigo: string) => obrasReporte.has(codigo);

  const horasTotal = horas.reduce((a, h) => a + Number(h.horas), 0);
  const costoPromHH = horasTotal > 0 ? montoNomina / horasTotal : 0;
  const m2Sem = reporte.totales.m2_semana;

  const subcPorObra = new Map<string, number>();
  for (const s of subc) {
    if (!enReporte(s.obra_codigo)) continue;
    subcPorObra.set(s.obra_codigo, (subcPorObra.get(s.obra_codigo) ?? 0) + Number(s.monto));
  }
  const moSubcontrato = [...subcPorObra.values()].reduce((a, v) => a + v, 0);

  const hhDirectas = horas
    .filter((h) => enReporte(h.obra_codigo))
    .reduce((a, h) => a + Number(h.horas), 0);
  const moDirecta = horasTotal > 0 ? montoNomina * (hhDirectas / horasTotal) : 0;

  const hhEquivSubc = costoPromHH > 0 ? moSubcontrato / costoPromHH : 0;
  const m2EquivSubc = teorico > 0 ? moSubcontrato / teorico : 0;

  const moTotalGastada = moDirecta + moSubcontrato;
  const moPresupuestada = teorico * m2Sem;
  const sobrecosto = moTotalGastada - moPresupuestada;
  const m2SinSubc = m2Sem - m2EquivSubc;
  const hhM2Sin = m2SinSubc > 0 ? hhDirectas / m2SinSubc : 0;
  const hhM2Con = m2Sem > 0 ? (hhDirectas + hhEquivSubc) / m2Sem : 0;
  const costoMOm2 = m2Sem > 0 ? moTotalGastada / m2Sem : 0;
  const eficiencia = costoMOm2 > 0 ? (teorico / costoMOm2) * 100 : 0;

  const codigos = new Set<string>([
    ...horas.filter((h) => enReporte(h.obra_codigo)).map((h) => h.obra_codigo),
    ...subcPorObra.keys(),
  ]);
  const obras = [...codigos]
    .map((codigo) => {
      const hrs = Number(horas.find((h) => h.obra_codigo === codigo)?.horas ?? 0);
      const pctHoras = horasTotal > 0 ? (hrs / horasTotal) * 100 : 0;
      const nominaAsig = horasTotal > 0 ? (hrs / horasTotal) * montoNomina : 0;
      const sc = subcPorObra.get(codigo) ?? 0;
      const moTotal = nominaAsig + sc;
      const m2 = m2PorObra.get(codigo) ?? 0;
      const costoM2 = m2 > 0 ? moTotal / m2 : 0;
      return { codigo, hrs, pctHoras, nominaAsig, sc, moTotal, m2, costoM2 };
    })
    .sort((a, b) => b.moTotal - a.moTotal);

  return {
    tieneNomina: !!nomina,
    teorico,
    m2Sem,
    hhDirectas,
    hhEquivSubc,
    costoPromHH,
    hhM2Sin,
    hhM2Con,
    costoMOm2,
    eficiencia,
    moDirecta,
    moSubcontrato,
    moTotalGastada,
    moPresupuestada,
    sobrecosto,
    obras,
  };
}
